import React, { useState, useRef, useEffect } from 'react';
import getConfig from 'next/config';
import Router, { useRouter } from 'next/router';
import Request from '@apester/requests';
import trackEvent from '../../utils/event-helper';
import AuthForm from '../../components/AuthForm/AuthForm';
import InputField from '../../components/InputField/InputField';
import AuthLayout from '../../layouts/AuthLayout/AuthLayout';
import Checkbox from '../../components/Checkbox/Checkbox';
import { useStateValue, setUser, setLoading } from '../../state/stateProvider';
import {
  isEmail, isPasswordValid, isPasswordsEquals, EMAIL_ERROR, PASSWORD_INFO, CONFIRM_ERROR,
} from '../../utils/auth-utils';
import { getUTMParams } from '../../utils/browser-utils';
import { linkifyContactEmail } from '../../utils/linkify';

const { publicRuntimeConfig: { config } } = getConfig();

const SignupPage = () => {
  const router = useRouter();
  const [emailError, setEmailError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [confirmError, setConfirmError] = useState<string>();
  const [error, setError] = useState<string>();
  const [isChecked, setIsChecked] = useState(false)
  const emailInput = useRef(null);
  const firstNameInput = useRef(null);
  const lastNameInput = useRef(null);

  const passwordInput = useRef(null);
  const passwordConfirmInput = useRef(null);
  const consentCheckbox = useRef(null);
  const [, dispatch] = useStateValue();

  // Redirect to login if no email query parameter
  useEffect(() => {
    if (router && router.isReady && !router.query.email) {
      Router.push('/auth/login');
    }
  }, [router]);

  // Pre-fill email from URL parameter
  useEffect(() => {
    if (!router) return;
    const emailParam = router.query.email as string;
    if (emailParam && emailInput.current) {
      emailInput.current.value = decodeURIComponent(emailParam);
    }
  }, [router]);

  const signup = (userObj) => (
    Request.Post(new URL(`${config.USERS_URL}/user`), userObj, { withCredentials: true })
  );
  const validateEmail = () => { setEmailError(!isEmail(emailInput?.current?.value) && EMAIL_ERROR); };

  const checkPassValid = () => {
    const password = passwordInput?.current?.value;
    const confPassword = passwordConfirmInput?.current?.value;
    const isPassValid = isPasswordValid(password);
    const isPassEquals = isPasswordsEquals(password, confPassword);
    setPasswordError(!isPassValid && PASSWORD_INFO);
    setConfirmError(!isPassEquals && CONFIRM_ERROR);
    return isPassValid && isPassEquals;
  };

  const isEmpty = () => !emailInput?.current?.value || !passwordInput?.current?.value 
    || !passwordConfirmInput?.current?.value || !firstNameInput?.current?.value 
    || !lastNameInput?.current?.value || !consentCheckbox?.current?.checked;

  const submitSignup = async (recaptchaVal = undefined) => {
    const userLoginData = {
      username: emailInput.current.value,
      email: emailInput.current.value,
      password: passwordInput.current.value,
      firstName: firstNameInput.current.value,
      lastName: lastNameInput.current.value,
      confirm: passwordConfirmInput.current.value,
      consent: consentCheckbox.current.checked,
      captcha: recaptchaVal || 'some-mock-recaptcha',
      source: getUTMParams(),
    };
    const clickedEventData = {
      userEmail: userLoginData.email,
      browser: navigator.appVersion,
    };
    trackEvent('user_registered_clicked', clickedEventData);
    setLoading(dispatch, true);
    setError(undefined);
    try {
      const signupResponse = await signup(userLoginData);
      const user = signupResponse.payload;
      setUser(dispatch, { ...user, isNew: true });
    } catch (e) {
      const errorData = {
        reason: e.message,
      };
      trackEvent('user_registration_failed', errorData);
      setLoading(dispatch, false);
      setError(e.message);
    }
  };

  const checkHandler = () => {
    setIsChecked(!isChecked)
  };

  const consentLabel = (
    <span>By clicking "CREATE ACCOUNT", you agree to Apester's&emsp;
      <a target="_blank" href="https://apester.com/terms-of-service">Terms of Service</a>
      &nbsp;and&nbsp;
      <a target="_blank" href="https://apester.com/privacy-policy">Privacy Policy</a>.
    </span>
  );

  return (
    <>
      <AuthForm
        className="signupForm"
        submitCallback={submitSignup}
        submitLabel="create account"
        getEmail={() => emailInput.current?.value}
        testId="signup-form"
        isDisabled={ Boolean(isEmpty() || emailError || passwordError || confirmError)}
        >
          <InputField style={{ gridRow: 1 }}
            refElement={firstNameInput}
            onClick={() => trackEvent('first_name_registration_clicked')}
            label="Name"
            name="given-name"
             />
          <InputField style={{ gridRow: 1 }}
            refElement={lastNameInput}
            onClick={() => trackEvent('last_name_registration_clicked')}
            label="Last name"
            name="family-name"
             />
          <InputField style={{ gridRow: 3, gridColumn: '1 / 3' }}
            refElement={emailInput}
            onClick={() => trackEvent('email_registration_clicked')}
            onChange={validateEmail}
            onBlur={validateEmail}
            hasError={emailError}
            name="email"
            type="email"
            label="Email" />
          <InputField style={{ gridRow: 5, gridColumn: '1 / 3' }}
            type="password"
            refElement={passwordInput}
            onClick={() => trackEvent('password_registration_clicked')}
            onChange={() => { checkPassValid(); }}
            hasError={passwordError}
            label="Password"
            name="password"
            info={PASSWORD_INFO}/>
          <InputField style={{ gridRow: 7, gridColumn: '1 / 3' }}
            refElement={passwordConfirmInput}
            type="password"
            onClick={() => trackEvent('confirm_password_registration_clicked')}
            onChange={() => { checkPassValid(); }}
            hasError={confirmError}
            name="confirm-password"
            label="Confirm password" />
          <Checkbox style={{ gridRow: 9, gridColumn: '1 / 3', width:'330px' }}
            onClick={() => trackEvent('confirm_terms_of_service_clicked')}
            onChange={() => { checkHandler(); }}
            name="consent"
            id="consent"
            isChecked={isChecked}
            className="disclaimer"
            required={true}
            refElement={consentCheckbox}
            label={consentLabel} />
      </AuthForm>
      <div className="error">
        {linkifyContactEmail(error)}
      </div>
    </>
  );
};

SignupPage.layout = AuthLayout;

export default SignupPage;
